export enum AccountingClassificationType {
  CurrentAssets = 'Current Assets',
  NonCurrentAssets = 'Non Current Assets',
  CurrentLiabilities = 'Current Liabilities',
  OtherCurrentLiabilities = 'Other Current Liabilities',
  NonCurrentLiabilities = 'Non Current Liabilities',
  Equity = 'Equity',
  Income = 'Income',
  CostOfGoodsSold = 'Cost Of Goods Sold',
  LaborExpenses = 'Labor Expenses',
  OperatingExpenses = 'Operating Expenses',
  Expenses = 'Expenses',
  OtherIncome = 'Other Income',
  OtherExpenses = 'Other Expenses',
}

export enum AccountingAccountType {
  CashInHand = 'Cash In Hand',
  CashAtBank = 'Cash At Bank',
  AccountsReceivable = 'Accounts Receivable',
  Inventory = 'Inventory',
  OtherCurrentAssets = 'Other Current Assets',
  FixedAssets = 'Fixed Assets',
  IntangibleAssets = 'Intangible Assets',
  OtherAssets = 'Other Assets',
  AccountsPayable = 'Accounts Payable',
  TradePayables = 'Trade Payables',
  SalariesPayable = 'Salaries Payable',
  UndistributedTips = 'Undistributed Tips',
  PayrollTaxPayable = 'Payroll Tax Payable',
  SalesTaxPayable = 'Sales Tax Payable',
  LongTermLiabilities = 'Long Term Liabilities',
  OtherCurrentLiabilities = 'Other Current Liabilities',
  Equity = 'Equity',
  AccumulatedProfits = 'Accumulated Profits',
  SalesOfProductIncome = 'Sales Of Product Income',
  OtherPrimaryIncome = 'Other Primary Income',
  ServiceFeeIncome = 'Service/Fee Income',
  DiscountsRefundsGiven = 'Discounts/Refunds Given',
  SuppliesAndMaterialsCOGS = 'Supplies & Materials COGS',
  OtherCostsOfServicesCOS = 'Other Costs Of Services COS',
  OperatingExpense = 'Operating Expense',
  AdsAndPromotion = 'Ads & Promotion',
  GeneralAndAdmin = 'General & Admin',
  OccupancyCost = 'Occupancy Cost',
  Taxes = 'Taxes',
  OtherMiscellaneousIncome = 'Other Miscellaneous Income',
  DividendIncome = 'Dividend Income',
  OtherMiscellaneousExpenses = 'Other Miscellaneous Expenses',
  OtherMiscellaneousExpense = 'Other Miscellaneous Expense',
  Depreciation = 'Depreciation',
  UnexplainedExpenses = 'Unexplained Expenses',
  UnexplainedIncome = 'Unexplained Income',
}

export interface SubGL {
  sub_gl_code: string;
  account_name: string;
  classification: string;
  account_type: string;
  account_balance: number;
}
export interface ChartsOfAccounts {
  id: number;
  gl_code: string;
  account_name: string;
  classification: string;
  account_type: string;
  account_balance: number;
  sub_gl: SubGL[];
}

export interface UpdateChartOfAccounts {
  id: number;
  credit: number;
  debit: number;
  createdBy?: string;
  updatedBy?: string;
}

export interface UpdateChartOfAccountsDto {
  id: number;
  changeInValue: number;
  transactionType: ChartOFAccountTransactionType;
  createdBy?: string;
  updatedBy?: string;
  description?: string;
  journalEntryId?: number;
}

export enum ChartOFAccountTransactionType {
  DEBIT = 'Debit',
  CREDIT = 'Credit',
}

export enum BankOnboardingPurposeEnum {
  PAYROLL_ISSUANCE = 'Payroll Issuance',
  SALES_COLLECTION = 'Sales Collection',
  VENDOR_PAYMENTS = 'Vendor Payments',
}

export enum ReconciliationDataSource {
  POS = 'POS Sales',
  BILL_PAYMENTS = 'Bill Payments',
  PAYROLLS = 'Payroll Payments',
}

export enum PlaidBankStatusEnum {
  CONNECTED = 'CONNECTED',
  USER_ACCOUNT_REVOKED = 'USER_ACCOUNT_REVOKED',
  PENDING_EXPIRATION = 'PENDING_EXPIRATION',
  USER_PERMISSION_REVOKED = 'USER_PERMISSION_REVOKED',
  PENDING_DISCONNECT = 'PENDING_DISCONNECT',
  NEW_ACCOUNTS_AVAILABLE = 'NEW_ACCOUNTS_AVAILABLE',
  ERROR = 'ERROR',
  OAUTH_CONSENT_EXPIRED = 'OAUTH_CONSENT_EXPIRED',
  ITEM_LOGIN_REQUIRED = 'ITEM_LOGIN_REQUIRED',
  INVALID_ACCESS_TOKEN = 'INVALID_ACCESS_TOKEN',
  LOGIN_REPAIRED = 'LOGIN_REPAIRED',
  WEBHOOK_UPDATE_ACKNOWLEDGED = 'WEBHOOK_UPDATE_ACKNOWLEDGED',
}
export enum ReconciliationActionTypes {
  MATCH = 'Match',
  VOID_MATCH = 'Void Match',
  VOID = 'Void',
  STALE_DATE = 'Stale Date',
  CASH_TRANSFER = 'Cash Transfer',
  JOURNAL_ENTRY = 'Journal Entry',
}

export enum ChartOfAccountMapping {
  host = '5210-01',
  manager = '5220-01',
  gm = '5220-01',
  janitorial = '5220-02',
  chef = '5220-03',
  'shift-or-assistant-manager' = '5220-04',
  security = '5220-05',
  'delivery-service-driver' = '5220-06',
  cook = '5200-01',
  'line-cook' = '5200-02',
  'dish-washer' = '5200-03',
  expeditor = '5200-04',
  server = '5210-02',
  busser = '5210-03',
  bartender = '5210-04',
  barback = '5210-05',
  cashier = '5210-06',
  accountant = '6003-03',
}

export interface IChartsOfAccountsLogDataResponse {
  source?: string;
  transactionType?: string;
  existingValue?: string;
  changeInValue?: string;
  newValue?: string;
  updatedAt?: number;
  nature?: string;
}

export interface IChartsOfAccountsGroupedLogDataResponse {
  id?: string;
  type?: string;
  date?: string;
}

export enum SalesControllerStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  ARCHIVED = 'Archived',
}

export const defaultChartsOfAccounts = [
  {
    parentGlId: null,
    glCodeId: '1001',
    name: 'Cash',
    classification: 'Current Assets',
    accountType: 'Cash In Hand',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: null,
    glCodeId: '1002',
    name: 'Bank',
    classification: 'Current Assets',
    accountType: 'Cash At Bank',
  },
  {
    parentGlId: null,
    glCodeId: '6001',
    name: 'Operation Cost (All Costs)',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: null,
    glCodeId: '1300',
    name: 'Inventory Asset',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1001',
    glCodeId: '1001-01',
    name: 'House Drawer Cash',
    classification: 'Current Assets',
    accountType: 'Cash In Hand',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1001',
    glCodeId: '1001-02',
    name: 'Cash In Safe',
    classification: 'Current Assets',
    accountType: 'Cash In Hand',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },

  {
    parentGlId: null,
    glCodeId: '1200',
    name: 'Accounts Receivable',
    classification: 'Current Assets',
    accountType: 'Accounts Receivable',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-01',
    name: 'Dairy',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-02',
    name: 'Meat',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-03',
    name: 'Seafood',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-04',
    name: 'Dry Goods',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-05',
    name: 'Poultry',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-06',
    name: 'Produce',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-07',
    name: 'NA Beverages',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-08',
    name: 'Liquor',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-09',
    name: 'Beer',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-10',
    name: 'Wine',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '1300',
    glCodeId: '1300-11',
    name: 'Bar Consumables',
    classification: 'Current Assets',
    accountType: 'Inventory',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: null,
    glCodeId: '2000',
    name: 'Accounts Payable',
    classification: 'Current Liabilities',
    accountType: 'Accounts Payable',
    increaseAmount: ChartOFAccountTransactionType.CREDIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-01',
    name: 'Supplies',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-02',
    name: 'Paper Products',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-03',
    name: 'Soap & Chemicals',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-04',
    name: 'Entertainment',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-05',
    name: 'Linen Service',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-06',
    name: 'Pest Control',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-07',
    name: 'Contract Labor',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-08',
    name: 'Travel Expense',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },

  {
    parentGlId: '6001',
    glCodeId: '6001-09',
    name: 'Meal Expense',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-10',
    name: 'Hiring Ads',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
  {
    parentGlId: '6001',
    glCodeId: '6001-11',
    name: 'Gas & Vehicle Expense',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },

  {
    parentGlId: '6001',
    glCodeId: '6001-12',
    name: 'Maintenance & Repairs',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },

  {
    parentGlId: '6001',
    glCodeId: '6001-13',
    name: 'Processing Fees',
    classification: 'Expenses',
    accountType: 'Operating Expense',
    increaseAmount: ChartOFAccountTransactionType.DEBIT,
  },
];

export enum AccountingScenarioType {
  INVOICE_SUBMISSION_FLOW = 'INVOICE_SUBMISSION_FLOW', // One shared flow for ingredient, non ingredient and non itemized invoices
  BILL_PAYMENT_FLOW = 'BILL_PAYMENT_FLOW',
  WASTAGE_LOG_FLOW = 'WASTAGE_LOG_FLOW',
  STOCK_ADJUSTMENT_FLOW = 'STOCK_ADJUSTMENT_FLOW',
  PAYROLL_FLOW = 'PAYROLL_FLOW',
  ORDER_PLACEMENT_FLOW = 'ORDER_PLACEMENT_FLOW',
  SALES_CONTROLLER_SUBMISSION_FLOW = 'SALES_CONTROLLER_SUBMISSION_FLOW',
  JOURNAL_ENTRY_SUBMISSION_FLOW = 'JOURNAL_ENTRY_SUBMISSION_FLOW',
  RECONCILIATION_FLOW = 'RECONCILIATION_FLOW',
  PAYROLL_PENDING_FLOW = 'PAYROLL_PENDING_FLOW',
  PAYROLL_PROCESSING_FLOW = 'PAYROLL_PROCESSING_FLOW',
  PAYROLL_REFUND_FLOW = 'PAYROLL_REFUND_FLOW',
  COOK_PREP_RECIPE_FLOW = 'COOK_PREP_RECIPE_FLOW',
  JOURNAL_ENTRY_FLOW = 'JOURNAL_ENTRY_FLOW',
  TENDER_PAYMENT_FLOW = 'TENDER_PAYMENT_FLOW',
}

export interface CreateIngredientLogDto {
  ingredientId: number;
  changeInQuantity: number;
  existingQuantityOnHand: number;
  newQuantityOnHand: number;
  changeInType: string;
  flowType: AccountingScenarioType;
  itemType: string;
  recipeLogId?: number;
  parentIngredientLogId?: number;
  unit?: number;
}
export interface CreatePrepRecipeLogDto {
  recipeId: number;
  changeInQuantity: number;
  existingQuantityOnHand: number;
  newQuantityOnHand: number;
  changeInType: string;
  flowType: AccountingScenarioType;
  itemType: string;
  recipeLogId?: number;
  source?: string;
  parentIngredientLogId?: number;
  unit?: number;
}
export interface ChartOfAccountSheetError {
  line: string;
  reason: string;
}
